import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Stack,
  Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import AppDialog from "../../AppComponents/AppDialog";
import AppAutoComplete from "../../AppComponents/AppAutoComplete";
import { AppButton } from "../../StyledComponent";
import { useOnboardClient } from "../../../hooks/useClientOnboarding";
import { InvestmentInput } from "../ProductOnboarding/components/InvestmentInput";

const OnboardClientModal = ({
  open,
  onClose,
  onSubmit,
  productId,
  familyList,
  isLoading,
}) => {
  const [selectedFamily, setSelectedFamily] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);

  const { mutate: onboardClient } = useOnboardClient(() => {
    resetForm();
    setSelectedFamily("");
    setSelectedMembers([]);
    onSubmit();
  });

  const generateValidationSchema = (members) => {
    const investmentsShape = {};
    members.forEach((memberId) => {
      investmentsShape[memberId] = Yup.object().shape({
        minInvestment: Yup.number()
          .transform((value, originalValue) =>
            String(originalValue).trim() === "" ? undefined : value
          )
          .typeError("Investment must be a number")
          .required("Investment is required"),
      });
    });

    return Yup.object().shape({
      investments: Yup.object().shape(investmentsShape),
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      investments: {},
    },
    validationSchema: generateValidationSchema(selectedMembers),
    onSubmit: (values) => {
      const payload = {
        productId,
        customerClients: selectedMembers.map((id) => ({
          customerClientNameId: id,
          investment: Number(values.investments[id]?.minInvestment),
        })),
        customerId: selectedFamily,
      };

      onboardClient(payload);
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setTouched,
    resetForm,
  } = formik;

  const handleFamilyChange = (e) => {
    setSelectedFamily(e.target.value);
    setSelectedMembers([]);
    resetForm();
  };

  const handleMemberToggle = (memberId) => {
    setSelectedMembers((prev) => {
      const updated = prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId];

      return updated;
    });
  };

  const handleInvestmentChange = (e, memberId) => {
    const { name, value } = e.target;

    setFieldValue(`investments.${memberId}.${name}`, value);
    setTouched((prev) => ({
      ...prev,
      investments: {
        ...(prev.investments || {}),
        [memberId]: {
          ...(prev.investments?.[memberId] || {}),
          [name]: true,
        },
      },
    }));
  };

  const selectedFamilyData =
    familyList && selectedFamily
      ? familyList.find((f) => f.id === selectedFamily)
      : null;

  const getError = (memberId) => {
    return touched?.investments?.[memberId]?.minInvestment &&
      errors?.investments?.[memberId]?.minInvestment
      ? errors.investments[memberId].minInvestment
      : null;
  };

  const handleCancel = () => {
    setSelectedFamily("");
    setSelectedMembers([]);
    resetForm();
    onClose();
  };

  const handleSubmitForm = () => {
    selectedMembers.forEach((id) => {
      if (!values.investments[id]) {
        setFieldValue(`investments.${id}.minInvestment`, "");
      }
    });

    const touchedFields = {};
    selectedMembers.forEach((id) => {
      touchedFields[id] = { minInvestment: true };
    });

    setTouched({
      investments: touchedFields,
    });

    handleSubmit();
  };

  return (
    <AppDialog open={open} onClose={handleCancel} title="Onboard Client">
      <Box sx={{ width: "100%", mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Select Family
        </Typography>

        <AppAutoComplete
          name="family"
          options={familyList || []}
          value={selectedFamily}
          onChange={handleFamilyChange}
          valueParam="id"
          labelParam="familyName"
          placeholder="Select"
          loading={isLoading}
        />

        {selectedFamilyData?.clientNames?.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Box display="flex" gap={2} mb={1}>
              <Typography variant="subtitle1" sx={{ width: "50%" }}>
                Entities
              </Typography>
              <Typography variant="subtitle1" sx={{ width: "50%" }}>
                Investment
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: "300px",
                overflowY: "auto",
                pr: 1,
              }}
            >
              {selectedFamilyData.clientNames.map((member) => {
                const isSelected = selectedMembers.includes(member.id);
                const isLongName = member.name.length > 30;

                const nameLabel = (
                  <Typography
                    noWrap={isLongName}
                    sx={{
                      maxWidth: isLongName ? 200 : "100%",
                      overflow: isLongName ? "hidden" : "visible",
                      textOverflow: isLongName ? "ellipsis" : "unset",
                      fontSize: "14px",
                    }}
                  >
                    {member.name}
                  </Typography>
                );

                return (
                  <Box
                    key={member.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ width: "50%" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleMemberToggle(member.id)}
                            sx={{ "&.Mui-checked": { color: "#BE8976" } }}
                          />
                        }
                        label={
                          isLongName ? (
                            <Tooltip
                              title={member.name}
                              slotProps={{
                                popper: {
                                  modifiers: [
                                    {
                                      name: "offset",
                                      options: {
                                        offset: [0, -14],
                                      },
                                    },
                                  ],
                                },
                              }}
                            >
                              {nameLabel}
                            </Tooltip>
                          ) : (
                            nameLabel
                          )
                        }
                        sx={{ margin: 0 }}
                      />
                    </Box>

                    <Box sx={{ width: "50%", maxWidth: 250 }}>
                      <InvestmentInput
                        isOnboardClientInvestment={"true"}
                        memberId={member.id}
                        currency={
                          values.investments?.[member.id]?.currency || "INR"
                        }
                        minInvestment={
                          values.investments?.[member.id]?.minInvestment || ""
                        }
                        onChange={(e) => handleInvestmentChange(e, member.id)}
                        touched={
                          touched?.investments?.[member.id]?.minInvestment
                        }
                        errors={getError(member.id)}
                        disabled={
                          !selectedFamily ||
                          !selectedMembers.includes(member.id)
                        }
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 4 }}
        >
          <AppButton
            onClick={handleSubmitForm}
            disabled={!selectedFamily || selectedMembers.length === 0}
          >
            Add
          </AppButton>
        </Stack>
      </Box>
    </AppDialog>
  );
};

export default OnboardClientModal;
